import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  HostListener,
  ChangeDetectionStrategy
} from "@angular/core";
import { AbstractControl, Validators } from "@angular/forms";

interface DropdownOption {
  value: string | number;
  label: string;
}

@Component({
    selector: "app-dropdown",
    templateUrl: "./dropdown.component.html",
    styleUrls: ["./dropdown.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DropdownComponent implements OnInit, OnChanges {
  @Input() label: string = "";
  @Input() options: (string | DropdownOption)[] = [];
  @Input() control: AbstractControl | null = null;
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() placeholder: string = "Select an option";
  @Input() selectedValue: string | null = null;
  @Input() dataId?: string;
  dropdownOpen: boolean = false;
  formattedOptions: DropdownOption[] = [];

  @ViewChild("dropdownToggle") dropdownToggle!: ElementRef;

  ngOnInit() {
    this.formatOptions();
    if (this.control) {
      if (this.required) {
        this.control.setValidators(Validators.required);
      } else {
        this.control.clearValidators();
      }
      this.control.updateValueAndValidity();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["options"]) {
      this.formatOptions();
    }
    if (this.control) {
      if (changes["required"]) {
        if (this.required) {
          this.control.setValidators(Validators.required);
        } else {
          this.control.clearValidators();
        }
        this.control.updateValueAndValidity();
      }
      if (changes["disabled"]) {
        this.disabled ? this.control.disable() : this.control.enable();
      }
    }
  }

  private formatOptions() {
    this.formattedOptions = this.options.map((option) => {
      if (typeof option === "string") {
        return { value: option, label: option };
      }
      return option as DropdownOption;
    });
  }

  get isDisabled(): boolean {
    return this.control && this.control.disabled
      ? this.control.disabled
      : false;
  }

  toggleDropdown() {
    if (!this.isDisabled) {
      this.dropdownOpen = !this.dropdownOpen;
    }
  }

  selectOption(option: DropdownOption) {
    if (this.control && !this.isDisabled) {
      this.control.setValue(option.value);
      this.dropdownOpen = false;
    }
  }

  getDisplayValue(): string {
    if (this.isDisabled && this.selectedValue) {
      return this.selectedValue;
    }
    if (this.control && this.control.value) {
      const selectedOption = this.formattedOptions.find(
        (opt) =>
          opt.value.toString() === this.control.value.toString() ||
          opt.label.toString() === this.control.value.toString()
      );
      const result = selectedOption ? selectedOption.label : this.placeholder;
      return result;
    }
    return this.placeholder;
  }

  @HostListener("document:click", ["$event.target"])
  onClickOutside(targetElement: EventTarget | null) {
    if (!(targetElement instanceof HTMLElement) || !this.dropdownToggle) return;

    if (
      !this.dropdownToggle.nativeElement.contains(targetElement) &&
      !targetElement.classList.contains("dropdown-option")
    ) {
      this.dropdownOpen = false;
    }
  }

  isPlaceholderShown(): boolean {
    return (
      !(this.isDisabled && this.selectedValue) &&
      !(this.control && this.control.value)
    );
  }
}
